import { useQuery } from '@apollo/client';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Card } from '@mui/material';
import { GridCallbackDetails, GridColDef, GridRow, GridRowModel, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { dateOnlyISO8601 } from '../../library/date';
import { gql } from '../../__generated__';
import { PersonStudentsWithLocationQueryVariables, PersonStudentsWithLocationQuery, StudentCustomField } from '../../__generated__/graphql';
import QuickSearchToolbar, { QuickSearchToolbarProps } from '../components/SearchBar/SearchBar';
import TablePaginated, { FetchMoreCallbackArgs } from '../Manage/components/TablePaginated/TablePaginated';
import usePersonStudentAssignment from './hooks/usePersonStudentAssignment';
import { useSelectedPerson } from '../../context/SelectedPersonContext';
import { useManageModal } from '../Manage/components/ManageModal';
import usePersonStore from '../store/personStore';

export const PERSON_STUDENT_WITH_LOCATION_QUERY = gql(`
 query personStudentsWithLocation ($personId:UUID!, $query:String, $first:Int, $after:String){
  person (id:$personId) {
    locationStudents (query:$query,status:ACTIVE, first:$first, after:$after) {
        pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
        edges{
            cursor
            node{
                id
                firstName
                lastName
                dob
                references
                location {
                    id
                    name
                }
                persons(ids:[$personId]){
                    edges{
                        cursor
                        node{
                            id
                        }     
                    }  
                }
            }
        }
      totalCount
    }
  }
}`);


type RouteParams = {
    pid?: string
}

type ActionOnCallback = () => void;

const createCustomField = (customField: any): GridColDef => {
    return {
        field: customField.name ?? 'missing',
        headerName: customField.name ?? 'missing',
        flex: 1,
        renderCell: (params) => {
            const studentCustomField = params.row.studentCustomFields
                .find((x: StudentCustomField) => x.customField?.id === customField.id) as StudentCustomField;

            return studentCustomField
                ? <>{studentCustomField.value}</>
                : null;
        }
    }
}

const createLocationField = (): GridColDef => {
    return {
        field: "location",
        headerName: "Location",
        sortable: true,
        flex: 1,
        renderCell: (params) => {
            return <>{params.row.location.name}</>
        },
        // valueGetter: (params: { row: any }) => `${params.row.location.name}`
    }
}

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}

const mapEdgesToTable = (data: any) => {
    return {
        ...data.node,
        id: data.node.id,
        formattedDob: format(parseISO(dateOnlyISO8601(data.node.dob)), 'MM/dd/yyyy'),
        student: data.node
        // dob: format(parseISO(dateOnlyISO8601(data.dob)), 'MM/dd/yyyy')
    }
}

function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {
    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

interface PersonTableLinkContainerProps {
    children: React.ReactNode
}

interface PersonTableLinkTableToolbarProps extends QuickSearchToolbarProps { }

interface PersonTableLinkProps {
    onBack: ActionOnCallback
}

interface PesonTableLinkFooterProps {
    saving: boolean,
    disabled: boolean,
    onBack: ActionOnCallback
    onSave: ActionOnCallback
}
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: 'name',
        headerName: "Child's Name",
        flex: 1,
        sortable: true,
        renderCell: (params: { row: any }) => <>{params.row.firstName} {params.row.lastName}</>
    },
    {
        field: 'dob',
        flex: 1,
        sortable: true,
        renderCell: (params: { row: any }) => <>{params.row.formattedDob}</>,
        headerName: 'Date of Birth',
    },
]
function arrayEquals(a: any, b: any) {
    if (a.length !== b.length) return false;
    return a.every((x: any, i: any) => x === b[i]);
}
const PersonTableLink = ({
    onBack
}: PersonTableLinkProps) => {
    const routeParams = useParams<RouteParams>();
    const locations = usePersonStore(state => state.locations)
    const { person } = useSelectedPerson(routeParams.pid);
    const isPaginatingRef = useRef(false);
    const { openModal } = useManageModal();
    const { loading, data, fetchMore } = useQuery<PersonStudentsWithLocationQuery, PersonStudentsWithLocationQueryVariables>(PERSON_STUDENT_WITH_LOCATION_QUERY, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            personId: routeParams?.pid,
            first: 3,
            query: ""
        }
    });

    const [tableState, setTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        initialSelectionModel: GridRowSelectionModel,
        selectionModel: GridRowSelectionModel,
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        },
    }>({
        columns: [],
        rows: [],
        initialSelectionModel: { type: 'include', ids: new Set() },
        selectionModel: { type: 'include', ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
    });

    const [saving, setSaving] = useState<boolean>(false);

    const { save } = usePersonStudentAssignment(routeParams.pid ?? '');
    // useEffect(() => {
    //     const visibleIds = tableState.rows.map(r => r.id);
    //     const pageChanged = !arrayEquals(prevVisibleIdsRef.current, visibleIds);
    //     if (pageChanged) {
    //         isPaginatingRef.current = true
    //         prevVisibleIdsRef.current = visibleIds;
    //     }
    // }, [tableState.rows])

    useEffect(() => {
        if (data) {
            setTableState((prevState) => {
                const columns = [...TABLE_COLUMNS]
                const rows = data?.person?.locationStudents?.edges?.map(mapEdgesToTable) ?? [];
                if (locations.length > 1) {
                    columns.push(createLocationField())
                }

                const currentPageIds = new Set(
                    data?.person.locationStudents?.edges?.map(edge => edge.node.id) ?? []
                );
                const selectionModel: GridRowSelectionModel = { type: 'include', ids: new Set(tableState.selectionModel.ids) };
                for (const edge of data?.person.locationStudents?.edges ?? []) {
                    const student = edge.node;
                    if (!student) continue;

                    const isAssigned = student.persons?.edges?.find((x: any) => x.node.id === routeParams.pid);

                    if (!isAssigned) {
                        continue;
                    }

                    // Only skip unassign if it's relevant to the current page
                    if (tableState.selections.unassign.some(x => currentPageIds.has(x) && x === student.id)) {
                        continue;
                    }
                    selectionModel.ids.add(student.id);
                }

                const _ids = Array.from(selectionModel.ids);
                return {
                    ...prevState,
                    columns,
                    rows: rows,
                    selectionModel,
                    selections: {
                        ...prevState.selections,
                        initial: _ids,
                        pageAssign: _ids,
                        prevAssign: unique(prevState.selections.prevAssign.concat(Array.from(selectionModel.ids))),
                        allSelected: Array.from(selectionModel.ids).concat(prevState.selections.nextAssign)
                    }
                }
            });
        }
    }, [loading, data]);
    const handleSelectionModelChange = (values: GridRowSelectionModel) => {
        if (isPaginatingRef.current) return; // ignore page-triggered changes

        const selectedIds = Array.from(values.ids);

        setTableState(prev => {
            const currentPageIds = new Set(prev.rows.map(r => r.id));
            const allSelected = new Set(prev.selections.allSelected);
            const prevPageSelected = Array.from(prev.selectionModel.ids);
            const assign = selectedIds.filter(id => !prevPageSelected.includes(id));
            const unassign = prevPageSelected.filter(id => !selectedIds.includes(id));


            // User selected rows -> add to global selection
            selectedIds.forEach(id => allSelected.add(id));

            // User deselected rows -> remove from global selection
            currentPageIds.forEach(id => {
                if (!selectedIds.includes(id)) allSelected.delete(id);
            });

            // Page-level selection for DataGrid display
            const pageSelectionIds = Array.from(allSelected).filter(id => currentPageIds.has(id));

            return {
                ...prev,
                selectionModel: { type: 'include', ids: new Set(pageSelectionIds) },
                selections: {
                    ...prev.selections, allSelected: Array.from(allSelected),
                    nextAssign: unique(deltaSelections(assign, unassign, prev.selections.nextAssign)),
                    unassign: deltaSelections(unassign, assign, prev.selections.unassign),
                },


            };
        });
    };

    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        isPaginatingRef.current = true
        await fetchMore({
            variables: {
                personIds: routeParams.pid,
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? 3
            }
        }).then((res) => {
            const rows = res?.data?.person?.locationStudents?.edges?.map(mapEdgesToTable) ?? []
            if (rows && rows.length > 0) {
                setTableState(prevState => ({
                    ...prevState,
                    rows
                }))
            }
        }).catch(err => {
            // console.log('the is an error: ', err)
        }).finally(() => {
            setTimeout(() => (isPaginatingRef.current = false), 200)
        })
    }
    const handleSave = async () => {

        // In theory, this save event shouldn't fire because the save button should be
        // disabled. However, for completeness sake, we exit the event early if 
        // there's nothing to save.
        if (tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)
            return;

        setSaving(true);

        // When not in development mode this debug code is removed (tree shaken).
        if (process.env.NODE_ENV === 'development') {
            console.group('handleSave');
            console.groupEnd();
        }

        await save(tableState.selections.nextAssign, tableState.selections.unassign);

        setTableState(prevState => ({
            ...prevState,
            selections: {
                ...prevState.selections,
                pageAssign: [],
                prevAssign: [],
                nextAssign: [],
                unassign: []
            }
        }))

        setSaving(false);

        openModal({
            type: 'SAVED_ASSIGNMENTS',
            onConfirm: undefined,
            cancelButtonLabel: 'Close'
        });
        // await fetchMore({
        //     variables: {
        //         first: 3,
        //         query: "",
        //         after: data?.person?.locationStudents?.pageInfo?.endCursor
        //     }
        // })
    }

    return (
        <PersonTableLink.Container>
            <TablePaginated
                loading={loading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <PersonTableLink.TableToolbar
                            title={!loading
                                ? `Link Child for ${person?.firstName} ${person?.lastName}`
                                : 'Loading...'
                            }
                            value={searchQuery}
                            onChange={onSearch}
                            onClickBack={onBack}
                            clearSearch={onSearchClear} />
                    )
                }}
                slots={{
                    row: (props: any) => {
                        const selected = tableState.selections.initial.includes(props.row.id);
                        const found = props.row.persons.edges?.find((x: any) => x.node.id === person?.id);
                        /*
                         * `props.selected` is not true when the row is disabled using `isRowSelectable` 🤦‍♂️
                         * so to achieve the desired result, we look at the intial selection modal i.e., 
                         * all identified person/student connections found at time of loeading the
                         * component
                         */
                        const disabled = props.row.references <= 1 && selected && found;
                        const handleDisabledClick = () => {
                            openModal({ type: 'NOT_ENOUGH_REFERENCES' })
                        };

                        return (
                            <div
                                style={{
                                    background: disabled ? '#F5F5F5' : 'initial',
                                    color: disabled ? '#777' : 'initial',
                                    cursor: disabled ? 'not-allowed' : 'default'
                                }}
                                onClick={disabled ? handleDisabledClick : undefined}>
                                <GridRow {...props} />
                            </div>
                        )
                    }
                }}
                isRowSelectable={(params) => {
                    const selected = tableState.selections.initial.includes(params.row.id);
                    const found = params.row.persons.edges?.find((x: any) => x.node.id === person?.id);
                    const disabled = !(params.row.references <= 1 && found && selected);
                    return disabled;
                }}
                pageInfo={data?.person?.locationStudents?.pageInfo}
                totalRows={data?.person?.locationStudents?.totalCount ?? 0}
                columns={tableState.columns}
                rows={tableState.rows}
                pageSize={3}
                selectionProps={{
                    rowSelectionModel: { type: 'include', ids: new Set(tableState.selections.allSelected) },
                    onRowSelectionModelChange: handleSelectionModelChange
                }}
                disableBoxShadow={true}
                checkboxSelection={true}
                onFetchMore={handleFetchMore}
                sortable={true} />
            <PersonTableLink.Footer
                disabled={(tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)}
                saving={saving}
                onBack={onBack}
                onSave={handleSave} />
        </PersonTableLink.Container>
    )
}

PersonTableLink.TableToolbar = (props: PersonTableLinkTableToolbarProps) => {
    return (
        <Box component="div">
            <QuickSearchToolbar {...props} />
        </Box>
    );
}

PersonTableLink.Container = ({ children }: PersonTableLinkContainerProps): React.ReactElement => {
    return (
        <Card
            elevation={4}
            style={{
                width: "100%",
                height: "auto",
            }}
        >
            <>{children}</>
        </Card>
    )
}

PersonTableLink.Footer = ({ disabled, saving, onSave, onBack }: PesonTableLinkFooterProps): React.ReactElement => {
    return (
        <Box
            component="div"
            sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}
        >
            <Button
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    marginRight: "16px",
                    backgroundColor: "#C4C4C4",
                    "&:hover": {
                        color: "#FFF",
                        backgroundColor: "red.main",
                    },
                }}
                onClick={onBack}
            >
                Cancel
            </Button>
            <LoadingButton
                loading={saving}
                disabled={disabled}
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light",
                    },
                }}
                onClick={onSave}
            >
                Save
            </LoadingButton>
        </Box>
    )
}

export default PersonTableLink;